import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';
import EfficientGraphExperiments from './EfficientGraphExperiments';
import styles from './Writing.module.css';

const githubSanitizeSchema = require('hast-util-sanitize/lib/github.json');
const sanitizeSchema = {
  ...githubSanitizeSchema,
  tagNames: [...githubSanitizeSchema.tagNames, 'aside'],
  attributes: {
    ...githubSanitizeSchema.attributes,
    div: [
      ...(githubSanitizeSchema.attributes.div || []),
      ['className', 'math', 'math-display'],
    ],
    span: [['className', 'math', 'math-inline']],
  },
};

function MarkdownLink({ href, children, ...props }) {
  const external = typeof href === 'string' && /^https?:\/\//i.test(href);

  return (
    <a
      {...props}
      {...(href ? { href } : {})}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

function MarkdownDiv({ className, ...props }) {
  const isDisplayMath = className?.split(/\s+/).includes('math-display');
  const scrollRegionProps = isDisplayMath
    ? {
        role: 'region',
        'aria-label': 'Scrollable mathematical formula',
        tabIndex: 0,
      }
    : {};

  return (
    <div
      {...props}
      {...(className ? { className } : {})}
      {...scrollRegionProps}
    />
  );
}

const EFFICIENT_GRAPH_SLUG = 'efficient-graph-neural';
const EXPERIMENTS_START = '\n## 5 Experiments\n';
const EXPERIMENTS_END = '\n---\n\n## 6 Discussion\n';

export function prepareArticleMarkdown(content, title) {
  const lines = String(content || '').split('\n');
  const firstContentIndex = lines.findIndex((line) => line.trim());
  const expectedHeading = typeof title === 'string' ? `# ${title.trim()}` : null;
  if (
    firstContentIndex !== -1 &&
    expectedHeading &&
    lines[firstContentIndex].trim() === expectedHeading
  ) {
    lines.splice(firstContentIndex, 1);
  }
  return lines.join('\n');
}

function MarkdownContent({ children }) {
  return (
    <ReactMarkdown
      components={{ a: MarkdownLink, div: MarkdownDiv, h1: 'h2' }}
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], rehypeKatex]}
    >
      {children}
    </ReactMarkdown>
  );
}

function splitExperimentsSection(content, articleSlug) {
  if (articleSlug !== EFFICIENT_GRAPH_SLUG) return null;

  const startIndex = content.indexOf(EXPERIMENTS_START);
  const endIndex = content.indexOf(EXPERIMENTS_END, startIndex + EXPERIMENTS_START.length);

  if (startIndex === -1 || endIndex === -1) return null;

  return {
    before: content.slice(0, startIndex),
    after: content.slice(endIndex),
  };
}

export default function MarkdownArticle({ articleSlug, content, title }) {
  const preparedContent = prepareArticleMarkdown(content, title);
  const splitContent = splitExperimentsSection(preparedContent, articleSlug);

  return (
    <div className={styles.markdown}>
      {splitContent ? (
        <>
          <MarkdownContent>{splitContent.before}</MarkdownContent>
          <EfficientGraphExperiments />
          <MarkdownContent>{splitContent.after}</MarkdownContent>
        </>
      ) : (
        <MarkdownContent>{preparedContent}</MarkdownContent>
      )}
    </div>
  );
}
